import { areExperimentControlsLoaded, getForcedVariant, getRolloutPct } from "./experimentControls";

/** Sticky, equal-weight UI assignment without waiting for analytics flags. */
const sessionAssignments = new Map();

/**
 * Stable 0-99 bucket per browser, used only when an admin has lowered the test
 * traffic below 100%. Read lazily so the normal split never touches it.
 */
let sessionBucket = null;
const rolloutBucket = () => {
  if (sessionBucket != null) return sessionBucket;
  try {
    const stored = Number(window.localStorage.getItem("22n_exp_bucket"));
    if (Number.isInteger(stored) && stored >= 0 && stored < 100) {
      sessionBucket = stored;
      return stored;
    }
    const next = Math.floor(Math.random() * 100);
    sessionBucket = next;
    window.localStorage.setItem("22n_exp_bucket", String(next));
    return next;
  } catch {
    sessionBucket = Math.floor(Math.random() * 100);
    return sessionBucket;
  }
};

const currentSearch = () => {
  try { return window.location.search; } catch { return ""; }
};

export function readExperimentOverride({ param, variants, search = currentSearch() }) {
  const value = new URLSearchParams(search).get(param);
  return variants.includes(value) ? value : null;
}

export function assignExperimentVariant({
  id,
  param,
  variants,
  storageKey = `22n_${id}`,
  search = currentSearch(),
}) {
  const params = new URLSearchParams(search);
  const override = params.get(param);
  const isValid = (value) => variants.includes(value);
  const pick = () => variants[Math.floor(Math.random() * variants.length)];

  // A review URL must never change the browser's real experiment assignment.
  if (params.get("preview") === "true") {
    return isValid(override) ? override : variants[0];
  }

  if (override === "reset") sessionAssignments.delete(storageKey);

  // A valid ?param=variant URL is a reviewer asking for one variant on purpose.
  if (isValid(override)) {
    sessionAssignments.set(storageKey, override);
    try {
      window.localStorage.setItem(storageKey, override);
    } catch {
      // Storage blocked: the session map still keeps the choice.
    }
    return override;
  }

  // Admin control wins over the local die roll. Nothing is persisted here, so
  // removing the force or raising the traffic lets the browser resume its own
  // assignment instead of being stuck on the admin's choice forever.
  const forced = getForcedVariant(id, variants);
  if (forced) return forced;

  // Before the switches have loaded we do not know the traffic setting, so show
  // the current design instead of handing this browser a variant. Nothing is
  // written, so the next page view joins the test properly. This is what stops
  // a first-time visitor from a WhatsApp link skipping the rollout.
  if (!areExperimentControlsLoaded()) return variants[0];

  const rollout = getRolloutPct(id);
  if (rollout <= 0) return variants[0];
  if (rollout < 100 && rolloutBucket() >= rollout) return variants[0];

  try {
    if (override === "reset") window.localStorage.removeItem(storageKey);

    const stored = window.localStorage.getItem(storageKey);
    if (isValid(stored)) {
      sessionAssignments.set(storageKey, stored);
      return stored;
    }

    const next = sessionAssignments.get(storageKey) || pick();
    sessionAssignments.set(storageKey, next);
    window.localStorage.setItem(storageKey, next);
    return next;
  } catch {
    // Blocked storage still keeps a stable choice across route remounts.
    const next = sessionAssignments.get(storageKey) || pick();
    sessionAssignments.set(storageKey, next);
    return next;
  }
}
